
import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogImageFallback?: string;
  canonicalUrl?: string;
  schema?: object;
  robots?: string;
  siteName?: string;
  ogImageAlt?: string;
  titleTemplate?: string | null;
  ogTitle?: string;
  ogDescription?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  prefetchLinks?: string[];
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  ogImage = "/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png",
  ogImageFallback,
  canonicalUrl,
  schema,
  robots = "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1",
  siteName = "Toc Toc",
  ogImageAlt = "Fotografia profissional de imóvel - Toc Toc",
  titleTemplate = " | Toc Toc - Fotografia Imobiliária Profissional",
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  prefetchLinks = [],
}) => {
  const siteUrl = "https://site.toctocbr.com";
  const fullTitle = titleTemplate ? `${title}${titleTemplate}` : title;
  const imageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  const fallbackUrl = ogImageFallback
    ? ogImageFallback.startsWith('http')
      ? ogImageFallback
      : `${siteUrl}${ogImageFallback}`
    : null;
  const ogTitleTag = ogTitle || fullTitle;
  const ogDescriptionTag = ogDescription || description;
  const twitterTitleTag = twitterTitle || fullTitle;
  const twitterDescriptionTag = twitterDescription || description;
  const canonicalTag = canonicalUrl
    ? canonicalUrl.startsWith("http")
      ? canonicalUrl
      : `${siteUrl}${canonicalUrl}`
    : siteUrl;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Essential SEO */}
      <meta name="robots" content={robots} />
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="sitemap" type="application/xml" href={`${siteUrl}/sitemap.xml`} />
      
      {/* Prefetch navigation links */}
      {prefetchLinks.map((link) => (
        <link key={link} rel="prefetch" href={link} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={ogTitleTag} />
      <meta property="og:description" content={ogDescriptionTag} />
      <meta property="og:image" content={imageUrl} />
      {fallbackUrl && <meta property="og:image" content={fallbackUrl} />}
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalTag} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitleTag} />
      <meta name="twitter:description" content={twitterDescriptionTag} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalTag} />

      {/* Geo targeting for local SEO */}
      <meta name="geo.region" content="BR-SC" />
      <meta name="geo.placename" content="Balneário Camboriú" />
      <meta name="geo.position" content="-26.9777,-48.6336" />
      <meta name="ICBM" content="-26.9777,-48.6336" />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
